/**
 * realizeScene — Build Three.js objects from a SceneDescriptor.
 *
 * Pure-ish function: takes a descriptor and Three.js dependencies,
 * creates meshes/lights/polylines, adds them to the scene, and
 * distributes object refs back to declaration nodes.
 *
 * Returns the objects added to scene (for cleanup by SceneNode).
 */

import type { Three, BufferGeometry } from './types';
import { mergeGeometries } from './geometryUtils';
import type {
  SceneDescriptor,
  MeshEntry,
  LightEntry,
  PolylineEntry
} from './buildSceneDescriptor';
import { logger } from '@/logging';

/** Result of realizing a scene descriptor */
export interface RealizedScene {
  /** Top-level objects added to scene — remove these to clear */
  objects: any[];
  /** Per-frame callbacks for nodes with function params (useExp) */
  animateCallbacks: (() => void)[];
}

/**
 * Realize a SceneDescriptor into Three.js objects on a scene.
 */
export function realizeScene(
  descriptor: SceneDescriptor,
  scene: any,
  three: Three,
  requestRebuild: () => void
): RealizedScene {
  const objects: any[] = [];

  for (const mesh of descriptor.meshes) {
    realizeMesh(mesh, scene, three, objects, requestRebuild);
  }
  for (const light of descriptor.lights) {
    realizeLight(light, scene, objects, requestRebuild);
  }
  for (const polyline of descriptor.polylines) {
    realizePolyline(polyline, scene, objects, requestRebuild);
  }
  // Add prebuilt objects directly (e.g. InstancedMesh from template Repeater)
  for (const obj of descriptor.prebuiltObjects) {
    scene.add(obj);
    objects.push(obj);
  }

  return { objects, animateCallbacks: descriptor.animateCallbacks };
}

function realizeMesh(
  entry: MeshEntry,
  scene: any,
  three: Three,
  objects: any[],
  requestRebuild: () => void
): void {
  let geometry: any;
  try {
    geometry = entry.buildGeometry();
  } catch (err) {
    logger.error(`[realizeScene] buildGeometry threw: ${err}`);
    return;
  }
  if (!geometry) {
    logger.error(`[realizeScene] buildGeometry returned null/undefined for material node ${entry.materialNodeRef.key}`);
    return;
  }

  // Handle array (multi-input merge case)
  if (Array.isArray(geometry)) {
    const geos = geometry.filter(Boolean) as BufferGeometry[];
    if (geos.length === 0) {
      logger.error(`[realizeScene] buildGeometry returned empty array for ${entry.materialNodeRef.key}`);
      return;
    }
    geometry = geos.length === 1 ? geos[0] : mergeGeometries(geos, 0);
    if (!geometry) {
      logger.error(`[realizeScene] mergeGeometries returned null for ${entry.materialNodeRef.key}`);
      return;
    }
  }

  let material: any;
  try {
    material = entry.buildMaterial();
  } catch (err) {
    logger.error(`[realizeScene] buildMaterial threw: ${err}`);
    return;
  }
  if (!material) {
    logger.error(`[realizeScene] buildMaterial returned null/undefined for ${entry.materialNodeRef.key}`);
    return;
  }

  logger.trace(`[realizeScene] creating mesh for ${entry.materialNodeRef.key}, geometry vertices=${geometry.attributes?.position?.count ?? '?'}`);
  const mesh = new three.Mesh(geometry, material);
  mesh.castShadow = entry.shadowConfig.castShadow;
  mesh.receiveShadow = entry.shadowConfig.receiveShadow;

  // Build transform hierarchy (transforms[0] is outermost)
  let topObject: any = mesh;
  let currentParent: any = null;

  for (let i = entry.transforms.length - 1; i >= 0; i--) {
    const t = entry.transforms[i];
    const group = new three.Group();
    t.apply(group);
    logger.trace(`[realizeScene] transform ${t.nodeRef.key} → pos=(${group.position.x.toFixed(2)}, ${group.position.y.toFixed(2)}, ${group.position.z.toFixed(2)})`);

    if (currentParent) {
      group.add(currentParent);
    } else {
      group.add(mesh);
    }
    currentParent = group;
    topObject = group;

    // Distribute Group ref to transform node
    t.nodeRef.node.setThreeObject(group);
    if (typeof t.nodeRef.node.setRebuildCallback === 'function') {
      t.nodeRef.node.setRebuildCallback(requestRebuild);
    }
  }

  scene.add(topObject);
  objects.push(topObject);

  // Distribute mesh ref to material node
  entry.materialNodeRef.node.setThreeObject(mesh);
  if (typeof entry.materialNodeRef.node.setRebuildCallback === 'function') {
    entry.materialNodeRef.node.setRebuildCallback(requestRebuild);
  }

  // Lightweight geometry refresh — re-runs the pipeline closure (including
  // boolean/merge ops) and swaps mesh.geometry in-place. No scene rebuild.
  const refreshGeometry = () => {
    try {
      let newGeo = entry.buildGeometry();
      if (!newGeo) return;
      if (Array.isArray(newGeo)) {
        const geos = (newGeo as any[]).filter(Boolean) as BufferGeometry[];
        if (geos.length === 0) return;
        newGeo = geos.length === 1 ? geos[0] : mergeGeometries(geos, 0);
        if (!newGeo) return;
      }
      const oldGeo = mesh.geometry;
      mesh.geometry = newGeo;
      if (oldGeo && typeof oldGeo.dispose === 'function') oldGeo.dispose();
    } catch (err) {
      logger.error(`[realizeScene] refreshGeometry threw: ${err}`);
    }
  };

  // Distribute refs to geometry source nodes (boolean, merge, leaf geometry)
  for (const ref of entry.geometryNodeRefs) {
    if (typeof ref.node.setThreeObject === 'function') {
      ref.node.setThreeObject(mesh);
    }
    if (typeof ref.node.setRebuildCallback === 'function') {
      ref.node.setRebuildCallback(refreshGeometry);
    }
  }
}

function realizeLight(
  entry: LightEntry,
  scene: any,
  objects: any[],
  requestRebuild: () => void
): void {
  let light: any;
  try {
    light = entry.buildLight();
  } catch (err) {
    logger.error(`[realizeScene] buildLight threw: ${err}`);
    return;
  }
  if (!light) {
    logger.error(`[realizeScene] buildLight returned null for ${entry.nodeRef.key}`);
    return;
  }

  scene.add(light);
  if (light.target) scene.add(light.target);
  objects.push(light);

  entry.nodeRef.node.setThreeObject(light);
  if (typeof entry.nodeRef.node.setRebuildCallback === 'function') {
    entry.nodeRef.node.setRebuildCallback(requestRebuild);
  }
}

function realizePolyline(
  entry: PolylineEntry,
  scene: any,
  objects: any[],
  requestRebuild: () => void
): void {
  let mesh: any;
  try {
    mesh = entry.buildMesh();
  } catch (err) {
    logger.error(`[realizeScene] buildMesh threw: ${err}`);
    return;
  }

  // In-place refresh: swap geometry+material on the existing mesh, dispose old ones.
  // Falls back to requestRebuild only if the mesh type changes (line ↔ tube).
  const refreshPolyline = () => {
    let newMesh: any;
    try {
      newMesh = entry.buildMesh();
    } catch (err) {
      logger.error(`[realizeScene] refreshPolyline buildMesh threw: ${err}`);
      return;
    }
    if (!newMesh) return;

    if (mesh && mesh.type === newMesh.type) {
      // Same object type — swap in place
      const oldGeo = mesh.geometry;
      const oldMat = mesh.material;
      mesh.geometry = newMesh.geometry;
      mesh.material = newMesh.material;
      if (oldGeo && typeof oldGeo.dispose === 'function') oldGeo.dispose();
      if (oldMat && typeof oldMat.dispose === 'function') oldMat.dispose();
    } else if (mesh) {
      // Type changed (e.g. LineSegments → Mesh) — need full rebuild
      requestRebuild();
    } else {
      // First mesh arriving after initial null — need full rebuild
      requestRebuild();
    }
  };

  if (typeof entry.nodeRef.node.setRebuildCallback === 'function') {
    entry.nodeRef.node.setRebuildCallback(refreshPolyline);
  }

  if (!mesh) {
    // Polyline data may not be available yet (async load) — not an error
    return;
  }

  scene.add(mesh);
  objects.push(mesh);

  entry.nodeRef.node.setThreeObject(mesh);
}
